'use client';

import { m } from 'framer-motion';
import NextLink from 'next/link';
import Image from 'next/image';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';

/**
 * Global Next.js App Router 404 page.
 * Rendered automatically by Next when no route matches, or when a server
 * component / page calls `notFound()`.
 */
export default function NotFound() {
  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        px: 2,
        py: { xs: 6, md: 10 },
        bgcolor: 'background.default',
        // Soft radial accent — picks up the brand blue without being loud.
        backgroundImage: (theme) =>
          `radial-gradient(circle at 20% 20%, ${theme.palette.primary.main}14, transparent 55%),
           radial-gradient(circle at 80% 80%, ${theme.palette.info.main}12, transparent 60%)`,
      }}
    >
      {/* Decorative blurred blobs */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: -120,
          right: -120,
          width: 360,
          height: 360,
          borderRadius: '50%',
          filter: 'blur(80px)',
          bgcolor: (t) => `${t.palette.primary.main}33`,
          zIndex: 0,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          bottom: -160,
          left: -100,
          width: 420,
          height: 420,
          borderRadius: '50%',
          filter: 'blur(90px)',
          bgcolor: (t) => `${t.palette.info.main}2a`,
          zIndex: 0,
        }}
      />

      <Container
        maxWidth="md"
        sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}
      >
        <Stack spacing={4} alignItems="center">
          <m.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              component="h1"
              sx={{
                fontWeight: 800,
                lineHeight: 1,
                fontSize: { xs: 120, sm: 180, md: 220 },
                background: (t) =>
                  `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.info.main})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: -4,
              }}
            >
              404
            </Typography>
          </m.div>

          <m.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Box
              sx={{
                position: 'relative',
                width: { xs: 220, sm: 280, md: 320 },
                height: { xs: 160, sm: 200, md: 240 },
                mx: 'auto',
              }}
            >
              <m.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
                style={{ width: '100%', height: '100%', position: 'relative' }}
              >
                <Image
                  src="/assets/illustrations/illustration-rocket-large.webp"
                  alt="Page not found"
                  fill
                  priority
                  style={{ objectFit: 'contain' }}
                  sizes="(max-width: 600px) 220px, 320px"
                />
              </m.div>
            </Box>
          </m.div>

          <m.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Stack spacing={1.5} alignItems="center">
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                We can&apos;t find that page
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 480 }}
              >
                The link you followed may be broken, or the page may have been
                moved. Let&apos;s get you back on track.
              </Typography>
            </Stack>
          </m.div>

          <m.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                component={NextLink}
                href="/"
                size="large"
                variant="contained"
                startIcon={<HomeRoundedIcon />}
                sx={{ borderRadius: 2, px: 3 }}
              >
                Go to home
              </Button>
              <Button
                size="large"
                variant="outlined"
                startIcon={<ArrowBackRoundedIcon />}
                onClick={() => {
                  if (typeof window !== 'undefined') window.history.back();
                }}
                sx={{ borderRadius: 2, px: 3 }}
              >
                Go back
              </Button>
            </Stack>
          </m.div>
        </Stack>
      </Container>
    </Box>
  );
}

